import {
  QueryObserverResult,
  UseMutationResult,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { APIClient } from 'api/APIClient';
import { QueryKeys } from 'constants/queryKeys';

export type ApproversListResult = {
  name: string;
  employeeId: string;
  approvesDepartments: {
    id: string;
    name: string;
  }[];
  active: boolean;
  id: number;
};

export type NewApproverQuery = {
  name: string;
  employeeId: string;
  approvesDepartments: ApprovesDepartment[];
  active: string;
  id?: string;
};

export interface ApprovesDepartment {
  name: string;
  id: string;
}
export interface Department {
  name: string;
  id: string;
  departments: Department[];
}

async function getDepartmentsList(): Promise<Department[]> {
  const { data } = await APIClient().get('departments');
  return data;
}
export function useApproversDepartmentsQuery(): QueryObserverResult<
  Department[],
  unknown
> {
  return useQuery({
    queryKey: [QueryKeys.APPROVERS_DEPARTMENTS],
    queryFn: () => getDepartmentsList(),
    enabled: true,
    staleTime: Infinity,
    retry: false,
    meta: {
      errorMessage: 'Ekki tókst að sækja samþykkjendur. Reyndu aftur síðar',
    },
  });
}

async function getApproversList(): Promise<ApproversListResult[]> {
  const { data } = await APIClient().get('approvers');
  return data;
}

export function useApproversListQuery(): QueryObserverResult<
  ApproversListResult[],
  unknown
> {
  return useQuery({
    queryKey: [QueryKeys.APPROVERS_LIST],
    queryFn: () => getApproversList(),
    enabled: true,
    staleTime: Infinity,
    retry: false,
    meta: {
      errorMessage: 'Ekki tókst að sækja samþykkjendur. Reyndu aftur síðar',
    },
  });
}

async function createApprover(query: NewApproverQuery): Promise<unknown> {
  const { data } = await APIClient().post('/approvers', query);
  return data;
}

export function useCreateApproverMutation(): UseMutationResult<
  unknown,
  unknown,
  NewApproverQuery,
  unknown
> {
  return useMutation<unknown, unknown, NewApproverQuery, unknown>({
    mutationFn: (query: NewApproverQuery) => createApprover(query),
    mutationKey: [QueryKeys.CREATE_APPROVER],
    meta: {
      successMessage: 'Samþykkjandi stofnaður.',
      errorMessage: 'Ekki tókst að stofna samþykkjanda. Reyndu aftur síðar',
    },
  });
}

async function updateApprover<T>(
  id: string | undefined,
  query: NewApproverQuery
): Promise<T> {
  if (typeof id === 'undefined') {
    throw new Error('TravelId is undefined');
  }
  const { data } = await APIClient().put(`/approvers/${id}`, query);
  return data;
}

export function useUpdateApproverQuery(
  id: string | undefined
): UseMutationResult<unknown, unknown, NewApproverQuery, unknown> {
  return useMutation<unknown, unknown, NewApproverQuery, unknown>({
    mutationFn: (query: NewApproverQuery) => updateApprover(id, query),
    mutationKey: [QueryKeys.UPDATE_APPROVER],
    meta: {
      successMessage: `Samþykkjandi hefur verið uppfærður.`,
      errorMessage: 'Ekki tókst að uppfæra samþykkjanda. Reyndu aftur síðar',
    },
  });
}

async function deleteApprover(id: string | null): Promise<unknown> {
  if (!id) {
    throw new Error('ID is undefined');
  }
  const { data } = await APIClient().delete(`approvers/${id}`);
  return data;
}

export function useDeleteApprover(id: string | null): UseMutationResult {
  return useMutation({
    mutationFn: () => deleteApprover(id),
    mutationKey: [QueryKeys.DELETE_APPROVER, id],
    meta: {
      successMessage: 'Samþykkjanda eytt.',
      errorMessage: 'Ekki tókst að eyða samþykkjanda. Reyndu aftur síðar',
    },
  });
}
